import { DownOutlined } from '@ant-design/icons';
import { Dropdown } from '@framework/ui/components/dropdown/Dropdown.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { Button, Space } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import { createGlobalStyle } from 'styled-components';
import { useSubmissionIdsFromUrl } from '../hooks/useSubmissionIdsFromUrl.ts';
import { type ISubmissionMenuItem } from './ISubmissionMenuItem.tsx';
import { SubmissionMenuStyled } from './SubmissionMenuStyled.tsx';
import { useSubmissionMenuPropsItems } from './useSubmissionMenuPropsItems.tsx';
import {
  isModuleSelected,
  useSubmissionMenuSelectedKey,
} from './useSubmissionMenuSelectedKey.ts';

export const DropdownSubmissionMenuGlobalStyle = createGlobalStyle`
  .root-menu-mobile-submission {
    .ant-dropdown-menu-submenu-title {
      display: flex;
      padding-inline-end: 12px !important;
    }
    .ant-dropdown-menu-submenu-expand-icon {
      display: none;
    }
  }
`;

export interface ISubmissionMenuProps {
  /** Moduli e Sezioni da visualizzare nel menu */
  items: ISubmissionMenuItem[];
  currentSubmissionModuleId: string;
}
/**
 * Menu laterale di navigazione
 */
export function SubmissionMenu(props: ISubmissionMenuProps) {
  const { items, currentSubmissionModuleId } = props;
  const isMobile = useBreakpoint(down('sm'));

  const selectedKey = useSubmissionMenuSelectedKey();
  const { t } = useTranslation();

  // Recupero gli ID dalla URL se presenti
  const { urlModuleId } = useSubmissionIdsFromUrl();

  const openKeys = useMemo(() => {
    // Se la chiave selezionata è un modulo, la seleziono come aperta per mostrare le sezioni
    if (isModuleSelected(selectedKey)) {
      return urlModuleId ? [urlModuleId] : [];
    }

    // Altrimenti non apro nessun menu
    return [];
  }, [selectedKey, urlModuleId]);

  const menuItems = useSubmissionMenuPropsItems({
    menu: items,
    currentSubmissionModuleId: isModuleSelected(selectedKey)
      ? currentSubmissionModuleId
      : null,
    excludeSections: isMobile ?? false,
  });

  const selectedMenuItem = menuItems.find(item => item.key === selectedKey);

  const selectedLabel = items.find(item => item.key === selectedKey)?.name;

  const selected = t('submission-menu.selezionato', 'selezionato');

  const menuAriaLabel =
    t('submission-menu.navigazione-richiesta', 'Navigazione della richiesta') +
    (selectedMenuItem ? `. ${selectedLabel} ${selected}.` : '');

  const selectedKeys = useMemo(() => {
    return selectedKey ? [selectedKey] : [];
  }, [selectedKey]);

  return (
    <>
      {isMobile ? (
        <>
          <DropdownSubmissionMenuGlobalStyle />
          <Dropdown
            menu={{
              items: menuItems,
              rootClassName: 'root-menu-mobile-submission',
              selectedKeys,
            }}
            placement="bottomLeft"
          >
            <Button aria-label={menuAriaLabel} size="large" block>
              <Space>
                <span>
                  {selectedMenuItem
                    ? selectedMenuItem.label
                    : t(
                        'submission-menu.navigazione-richiesta',
                        'Navigazione della richiesta',
                      )}
                </span>
                <DownOutlined aria-hidden="true" />
              </Space>
            </Button>
          </Dropdown>
          <VerticalSpace />
        </>
      ) : (
        <>
          {/* <VerticalSpace /> */}
          <SubmissionMenuStyled
            aria-label={menuAriaLabel}
            // autoFocus
            mode="inline"
            // ID Duplicato per WAI-ARIA (duplicated ID)
            forceSubMenuRender={false}
            // inlineCollapsed={false}
            style={{ height: '100%' }} // borderRight: 0
            items={menuItems}
            openKeys={openKeys}
            selectedKeys={selectedKeys}
            expandIcon={null}
          />
          {/*  Mostrare il componente successivo per visualizzazione il menu di test degli stati */}
          {/* <MenuTestStatiCompilazioneModuli /> */}
        </>
      )}
    </>
  );
}
